import {
  Container,
  CurrentSelectionLabel,
  FullNameLabel,
  ButtonsWrapper,
  OtherOptionsLabel,
  RowCenterSpaceBetween,
  chevronRotateStyle,
  OtherOptionsWrapper,
  OtherOptionsRow,
  OtherOptionLabel,
  SelectLabel,
  NoContentLabel,
  NoContentLabelWrapper,
  OtherOptionsWrapperScrollable,
  inputWrapperStyling,
  ToggleDarkModeWrapperAbsolute,
} from './ProfileDropdown.styled';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useTranslation } from 'react-i18next';
import { useGetCompaniesInfinite, useLogout, useSelectCompany } from './hooks';
import Button from 'components/Button/Button';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { COMPANIES_PER_PAGE } from './constants';
import { CaretDown, MagnifyingGlass as Search } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import { CompanyRole, ICompany } from 'types/Company.types';
import { useLoadNextPage } from 'utils/hooks/useLoadNextPage';
import Input from 'components/Input/Input';
import { useDebounce } from 'utils/hooks/useDebounce';
import { AppMode } from 'store/Common/reducers/common';
import { getDataFromPages } from 'utils/getDataFromPages';
import { Skeleton } from '@mui/material';
import ToggleDarkMode from 'components/ToggleDarkMode/ToggleDarkMode';
import SwitchAppMode from 'components/SwitchAppMode/SwitchAppMode';
import { handleSelectCompany } from './helpers';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import {
  getAppModeBasedOnCompanyRole,
  getLogoNavigationDestinationBasedOnAppMode,
} from '../helpers';

interface IProps {
  setShowProfileDropdown: Dispatch<SetStateAction<boolean>>;
}

const ProfileDropdown = ({ setShowProfileDropdown }: IProps) => {
  const navigate = useNavigate();
  const loadMoreRefCompanies = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchByCompany, setSearchByCompany] = useState<string>('');
  const debouncedSearchByCompany = useDebounce(searchByCompany);
  const { first_name, last_name, company } = useSelector(
    (state: IRootReducerState) => state.userInfo
  );
  const [isCompaniesDropdownExpanded, setIsCompaniesDropdownExpanded] =
    useState<boolean>(false);

  const { appMode } = useSelector(
    (state: IRootReducerState) => state.commonInfo
  );

  // onSettled clears local storage
  const { mutate: logout, isLoading } = useLogout();

  const {
    data: companiesData,
    hasNextPage: hasNextPageCompanies,
    fetchNextPage: fetchNextPageCompanies,
    isFetching: isFetchingCompanies,
    isLoading: isLoadingCompanies,
  } = useGetCompaniesInfinite(COMPANIES_PER_PAGE, debouncedSearchByCompany);
  const companies: ICompany[] = getDataFromPages(
    companiesData?.pages,
    'companies'
  ).filter(
    (alternativeCompany: ICompany) => alternativeCompany.id !== company?.id
  );

  useLoadNextPage(
    loadMoreRefCompanies,
    hasNextPageCompanies,
    fetchNextPageCompanies,
    isFetchingCompanies
  );

  const { mutate: selectCompany } = useSelectCompany();

  return (
    <Container>
      <ToggleDarkModeWrapperAbsolute>
        <ToggleDarkMode />
      </ToggleDarkModeWrapperAbsolute>
      <FullNameLabel>
        {first_name} {last_name}
      </FullNameLabel>

      {(appMode === AppMode.BUYER || appMode === AppMode.SUPPLIER) && (
        <>
          <CurrentSelectionLabel>
            {company ? company.name : t('No company selected')}
          </CurrentSelectionLabel>
          {companies.length >= 1 && (
            <RowCenterSpaceBetween
              onClick={() =>
                setIsCompaniesDropdownExpanded(!isCompaniesDropdownExpanded)
              }
            >
              <OtherOptionsLabel>
                {t('Other company options')}
              </OtherOptionsLabel>
              <Icon
                svg={CaretDown}
                size={20}
                wrapperStyle={
                  isCompaniesDropdownExpanded ? chevronRotateStyle : null
                }
                color={COLORS.BLACK}
              />
            </RowCenterSpaceBetween>
          )}
          <OtherOptionsWrapper isExpanded={isCompaniesDropdownExpanded}>
            <Input
              placeholder={t('Search companies')}
              width={'100%'}
              onChange={(e: any) => setSearchByCompany(e.target.value)}
              wrapperStyles={inputWrapperStyling}
              icon={Search}
            />
            <OtherOptionsWrapperScrollable>
              {companies.length ? (
                companies?.map((company: ICompany, index: number) => (
                  <OtherOptionsRow
                    isLast={index === companies.length}
                    key={`company-option-${index}`}
                    onClick={() => {
                      handleSelectCompany(
                        dispatch,
                        selectCompany,
                        company,
                        t,
                        appMode
                      );
                      navigate(
                        getLogoNavigationDestinationBasedOnAppMode(
                          getAppModeBasedOnCompanyRole(company.role, appMode)
                        )
                      );
                    }}
                  >
                    <OtherOptionLabel>{company?.name}</OtherOptionLabel>
                    <SelectLabel>{t('Select')}</SelectLabel>
                  </OtherOptionsRow>
                ))
              ) : isLoadingCompanies ? (
                <>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </>
              ) : (
                <NoContentLabelWrapper>
                  <NoContentLabel>{t('No companies found')}</NoContentLabel>
                </NoContentLabelWrapper>
              )}
              <div
                ref={loadMoreRefCompanies}
                style={{
                  height: '1rem',
                }}
              />
            </OtherOptionsWrapperScrollable>
          </OtherOptionsWrapper>
        </>
      )}

      {company?.role === CompanyRole.BOTH &&
        appMode !== AppMode.EXTERNAL &&
        appMode !== AppMode.DEALER_PANEL && <SwitchAppMode />}

      <ButtonsWrapper>
        <Button
          secondary
          width="100%"
          onClick={() => {
            navigate(RoutesConfig.Profile.fullPath);
            setShowProfileDropdown(false);
          }}
          label={t('Manage profile')}
        />
        <Button
          disabled={isLoading}
          redOutline
          onClick={() => logout()}
          label={t('Log out')}
          width={'100%'}
        />
      </ButtonsWrapper>
    </Container>
  );
};

export default ProfileDropdown;
